import type {
    ConsentDataResponse,
    ConsentItem,
    ConsentItemRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/consent/api/consent.api.ts';
import type { FormField } from '@types';
import { isUtcDateAfter } from '@utils/date.ts';
import { isEqual } from 'lodash';

export const getMappedConsentFormData = (consents: ConsentItem[]): Record<number, boolean> =>
    consents.reduce(
        (acc, c) => {
            acc[c.id] = c.isActive;

            return acc;
        },
        {} as Record<number, boolean>,
    );

export const isConsentDisabled = (formFields: FormField[], fieldId: number) => {
    if (formFields.length === 0) return false;

    const targetConsent = formFields.find((field) => Number(field.name[0]) === fieldId);

    return targetConsent && targetConsent.value !== undefined ? !targetConsent.value : false;
};

export const getConsentItemTextAsHtml = (title: string, description: string) => `<b>${title}</b>: ${description}`;

export const mapToRequestConsentData = (items: ConsentItem[], values: Record<string, boolean>): ConsentItemRequest[] =>
    items.map(({ id, title, description, wasAdded, isActive }) => ({
        id: wasAdded ? null : id,
        title,
        description,
        isActive: values[String(id)] ?? isActive ?? false,
    }));

export const buildInitialFormFields = (values: Record<string | number, boolean>): FormField[] =>
    Object.entries(values).map(([key, val]) => ({
        name: [String(key)],
        value: Boolean(val),
        touched: false,
        validating: false,
        errors: [],
        warnings: [],
        validated: true,
    }));

export const syncConsentActiveWithForm = (items: ConsentItem[], values: Record<string, boolean>): ConsentItem[] =>
    items.map((i) => ({
        ...i,
        isActive: values[String(i.id)] ?? i.isActive,
    }));

const mapConsentForComparison = (list: ConsentDataResponse | null) =>
    (list ?? [])
        .map(({ id, title, description, isActive }) => ({
            id,
            title,
            description,
            isActive,
        }))
        .sort((a, b) => a.id - b.id);

export const hasConsentDataChanged = (a: ConsentDataResponse | null, b: ConsentDataResponse | null) =>
    !isEqual(mapConsentForComparison(a), mapConsentForComparison(b));

export const wasStepAlreadyPublished = (data: ConsentDataResponse): boolean =>
    data.some(
        (item) => item.lastModifiedUTC && item.createdAtUTC && isUtcDateAfter(item.lastModifiedUTC, item.createdAtUTC),
    );
